import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signUp } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext'; // Fixed: changed from '../context/AuthContext'
import logo from '../assets/img/logo/logo.png';
import TermsAcceptanceCheckbox from '../components/TermsAcceptanceCheckbox';

import { checkNewCompanyName, lookupInvite } from '../services/apiService';

// Cognito prefixes errors from the pre-sign-up trigger; show just the trigger's message.
const cleanSignUpError = (message = '') =>
  message.replace(/^PreSignUp failed with error\s*/i, '').replace(/\.\.$/, '.');

const SignUp = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    newCompanyName: '',
    password: '',
    confirmPassword: '',
  });
  // Joining an existing company is only possible through an invite link (?invite=<token>).
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [invite, setInvite] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteToken));
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!inviteToken) return;
    lookupInvite(inviteToken)
      .then((details) => {
        setInvite(details);
        setFormData(prev => ({ ...prev, email: details.email }));
      })
      .catch((err) => setError(err.message))
      .finally(() => setInviteLoading(false));
  }, [inviteToken]);

  useEffect(() => {
    // Redirect authenticated users away from sign-up page
    if (!loading && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter your full name');
      return false;
    }

    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (inviteToken && !invite) {
      setError('This invite link is invalid or has expired. Ask your company admin for a new one.');
      return false;
    }

    if (invite && formData.email.trim().toLowerCase() !== invite.email) {
      setError(`This invite is for ${invite.email}. Please sign up with that address.`);
      return false;
    }

    if (!invite && !formData.newCompanyName.trim()) {
      setError('Please enter your company name');
      return false;
    }

    if (!formData.password) {
      setError('Please enter a password');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!termsAccepted) {
      setError('Please accept the Terms and Conditions to continue');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    // Early warning for a taken company name; the pre-sign-up trigger enforces it too.
    if (!invite) {
      const check = await checkNewCompanyName(formData.newCompanyName).catch((err) => {
        console.error("Error checking company name:", err);
        return { allowed: false, reason: "Unable to verify your company name. Please try again." };
      });
      if (!check.allowed) {
        setError(check.reason);
        setIsLoading(false);
        return;
      }
    }

    const companyAttributes = invite
      ? { 'custom:companyId': invite.companyId, 'custom:companyName': invite.companyName }
      : { 'custom:companyName': formData.newCompanyName.trim(), 'custom:companyId': `company-${crypto.randomUUID()}` };

    try {
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            name: formData.name,
            ...companyAttributes
          },
          // The pre-sign-up trigger checks this token before Cognito creates the account.
          ...(invite && { clientMetadata: { inviteToken } }),
        }
      });

      navigate('/signin', { state: { message: 'Account created successfully! Please check your email to confirm your account before signing in.' } });

    } catch (error) {
      setError(cleanSignUpError(error.message) || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  // Show loading spinner while checking authentication or the invite link
  if (loading || inviteLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img className="h-12 w-auto" src={logo} alt="ValuePay.ai" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
        {invite && (
          <p className="mt-2 text-center text-sm text-gray-600">
            You've been invited to join <span className="font-semibold">{invite.companyName}</span>
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  readOnly={Boolean(invite)}
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm read-only:bg-gray-100"
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            {invite ? (
              <div>
                <span className="block text-sm font-medium text-gray-700">Company</span>
                <p className="mt-1 px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-sm text-gray-700">
                  {invite.companyName}
                </p>
              </div>
            ) : (
              <div>
                <label htmlFor="newCompanyName" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <div className="mt-1">
                  <input
                    id="newCompanyName"
                    name="newCompanyName"
                    type="text"
                    autoComplete="organization"
                    required
                    value={formData.newCompanyName}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  This registers a new company. To join an existing one, use the invite link from your company admin.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Password must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm your password"
                />
              </div>
            </div>

            {/* Terms and Conditions Acceptance */}
            <div className="pt-2">
              <TermsAcceptanceCheckbox
                isAccepted={termsAccepted}
                onAcceptanceChange={setTermsAccepted}
              />
            </div>

            {error && (
              <div className="text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={handleSignIn}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;